import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CheckCircle2, Tag, X } from 'lucide-react';
import { shopApi } from '@/api';
import type { BuyResult, Product, ProductDuration } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn, formatRub } from '@/lib/utils';

const PROMO_ERR: Record<string, string> = {
  invalid: 'Промокод не найден',
  inactive: 'Промокод отключён',
  expired: 'Срок действия истёк',
  not_yet_active: 'Промокод ещё не активен',
  limit_reached: 'Промокод исчерпан',
  user_limit_reached: 'Вы уже использовали этот промокод',
  min_amount_not_reached: 'Сумма заказа слишком мала для этого промокода',
};

interface BuyDialogProps {
  product: Product;
  open: boolean;
  onClose: () => void;
  onSuccess: (r: BuyResult) => void;
  userBalanceMinor: number;
}

export function BuyDialog({ product, open, onClose, onSuccess, userBalanceMinor }: BuyDialogProps) {
  const tiers: ProductDuration[] = product.durations ?? [];
  const hasTiers = tiers.length > 0;
  const onlyTier: ProductDuration | undefined = tiers.length === 1 ? tiers[0] : undefined;

  const [tierIdx, setTierIdx] = useState<number>(0);
  const [promo, setPromo] = useState('');
  const [promoErr, setPromoErr] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setTierIdx(0);
    setPromo('');
    setPromoErr(null);
    setPromoDiscount(null);
  }, [open, product.id]);

  // При смене тарифа сбрасываем применённый промокод (его нужно перепроверить).
  useEffect(() => {
    setPromoDiscount(null);
    setPromoErr(null);
  }, [tierIdx]);

  const basePrice = useMemo(() => {
    if (hasTiers) return tiers[tierIdx]?.priceMinor ?? product.priceMinor;
    return product.priceMinor;
  }, [hasTiers, tiers, tierIdx, product.priceMinor]);

  const finalPrice = Math.max(0, basePrice - (promoDiscount ?? 0));

  const checkPromoMut = useMutation({
    mutationFn: () =>
      shopApi.checkPromo({
        productId: product.id,
        durationIndex: hasTiers ? tierIdx : undefined,
        code: promo.trim(),
      }),
    onSuccess: (r) => {
      setPromoDiscount(r.discountMinor);
      setPromoErr(null);
    },
    onError: (err: AxiosError<{ error?: string }>) => {
      const code = err.response?.data?.error;
      setPromoDiscount(null);
      setPromoErr(PROMO_ERR[code ?? ''] ?? 'Не удалось применить промокод');
    },
  });

  const buyMut = useMutation({
    mutationFn: () =>
      shopApi.buy({
        productId: product.id,
        durationIndex: hasTiers ? tierIdx : undefined,
        promoCode: promoDiscount != null && promo.trim() ? promo.trim() : undefined,
      }),
    onSuccess: (r) => onSuccess(r),
  });

  const insufficient = userBalanceMinor < finalPrice;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Покупка: {product.name}</DialogTitle>
          {product.description && (
            <DialogDescription className="whitespace-pre-line">{product.description}</DialogDescription>
          )}
        </DialogHeader>

        {hasTiers && tiers.length > 1 && (
          <div>
            <p className="text-xs uppercase font-semibold tracking-wider text-eol-desc mb-2">
              Выберите срок
            </p>
            <div className="space-y-2">
              {tiers.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setTierIdx(i)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 rounded-md border text-sm transition',
                    tierIdx === i
                      ? 'border-eol-pink/60 bg-eol-pink/10 text-eol-white'
                      : 'border-white/10 text-eol-desc hover:text-eol-white hover:bg-white/5',
                  )}
                >
                  <span className="font-semibold">{durationLabel(t)}</span>
                  <span className="font-bold text-eol-pink">{formatRub(t.priceMinor)}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {onlyTier && (
          <div className="flex items-center justify-between rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm">
            <span className="text-eol-desc">Срок: <span className="text-eol-white font-semibold">{durationLabel(onlyTier)}</span></span>
            <span className="font-bold text-eol-pink">{formatRub(onlyTier.priceMinor)}</span>
          </div>
        )}

        <div>
          <p className="text-xs uppercase font-semibold tracking-wider text-eol-desc mb-2 flex items-center gap-2">
            <Tag className="h-3 w-3" /> Промокод (если есть)
          </p>
          <div className="flex gap-2">
            <Input
              value={promo}
              onChange={(e) => {
                setPromo(e.target.value.toUpperCase());
                setPromoDiscount(null);
                setPromoErr(null);
              }}
              placeholder="WINTER25"
              className="flex-1 uppercase"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => promo.trim() && checkPromoMut.mutate()}
              disabled={!promo.trim() || checkPromoMut.isPending || promoDiscount != null}
            >
              {promoDiscount != null ? <CheckCircle2 className="h-4 w-4" /> : 'Применить'}
            </Button>
          </div>
          {promoErr && (
            <p className="mt-1 text-xs text-eol-red flex items-center gap-1">
              <X className="h-3 w-3" /> {promoErr}
            </p>
          )}
          {promoDiscount != null && (
            <p className="mt-1 text-xs text-eol-green flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Скидка {formatRub(promoDiscount)} применена
            </p>
          )}
        </div>

        <div className="rounded-md bg-eol-black/40 border border-white/5 p-3 space-y-1 text-sm">
          <div className="flex items-center justify-between text-eol-desc">
            <span>Цена</span>
            <span>{formatRub(basePrice)}</span>
          </div>
          {promoDiscount != null && promoDiscount > 0 && (
            <div className="flex items-center justify-between text-eol-green">
              <span>Скидка</span>
              <span>−{formatRub(promoDiscount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-base pt-1 border-t border-white/10">
            <span className="text-eol-white font-bold">Итого</span>
            <span className="text-eol-pink font-extrabold text-lg">{formatRub(finalPrice)}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-eol-desc">
            <span>Ваш баланс</span>
            <span className={insufficient ? 'text-eol-red font-semibold' : ''}>
              {formatRub(userBalanceMinor)}
              {!insufficient && (
                <> → <span className="text-eol-white">{formatRub(userBalanceMinor - finalPrice)}</span></>
              )}
            </span>
          </div>
          {insufficient && (
            <Badge variant="danger" className="mt-2">Недостаточно средств - пополните баланс</Badge>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Отмена</Button>
          <Button
            onClick={() => buyMut.mutate()}
            disabled={buyMut.isPending || insufficient}
            className="min-w-[140px]"
          >
            {buyMut.isPending ? 'Покупаем…' : `Купить за ${formatRub(finalPrice)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function durationLabel(t: ProductDuration): string {
  if (t.label) return t.label;
  if (t.days === 0) return 'Навсегда';
  if (t.days % 365 === 0) return `${t.days / 365} г.`;
  if (t.days % 30 === 0) return `${t.days / 30} мес.`;
  if (t.days % 7 === 0) return `${t.days / 7} нед.`;
  return `${t.days} дн.`;
}
